import React from 'react';
import { Edit3, Trash2, Search } from 'lucide-react';
import { GiftRecord } from '../../../types/invitation';
import ThemedCard from '../../themed/ThemedCard';
import GuestSideTag from '../../GuestSideTag';
import { formatAmount, formatGiftType } from '../types';

interface RecordsTabProps {
  giftRecords: GiftRecord[];
  filteredRecords: GiftRecord[];
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  deleting: string | null;
  onEdit: (record: GiftRecord) => void;
  onDelete: (recordId: string) => void;
}

export const RecordsTab: React.FC<RecordsTabProps> = ({
  giftRecords,
  filteredRecords,
  searchTerm,
  setSearchTerm,
  deleting,
  onEdit,
  onDelete,
}) => {
  return (
    <ThemedCard className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-wedding-text-body" />
          <input
            type="text"
            placeholder="搜尋賓客姓名或電子郵件..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-wedding-pink/30 rounded-lg focus:ring-2 focus:ring-wedding-pink focus:border-wedding-pink"
          />
        </div>
        <span className="text-sm text-wedding-text-body ml-4">
          顯示 {filteredRecords.length} / {giftRecords.length} 筆記錄
        </span>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-wedding-pink/20">
              <th className="text-left py-3 px-4 font-semibold text-wedding-text-title">賓客姓名</th>
              <th className="text-left py-3 px-4 font-semibold text-wedding-text-title">親友分類</th>
              <th className="text-left py-3 px-4 font-semibold text-wedding-text-title">金額</th>
              <th className="text-left py-3 px-4 font-semibold text-wedding-text-title">類型</th>
              <th className="text-left py-3 px-4 font-semibold text-wedding-text-title">記錄時間</th>
              <th className="text-left py-3 px-4 font-semibold text-wedding-text-title">操作</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.map((record) => (
              <tr key={record.id} className="border-b border-wedding-pink/10 hover:bg-wedding-pink/5">
                <td className="py-3 px-4">
                  <div>
                    <div className="font-medium text-wedding-text-title">{record.guestName}</div>
                    {record.guestEmail && (
                      <div className="text-sm text-wedding-text-body">{record.guestEmail}</div>
                    )}
                  </div>
                </td>
                <td className="py-3 px-4">
                  <GuestSideTag
                    guestSide={record.guestSide || 'groom'}
                    readonly={true}
                  />
                </td>
                <td className="py-3 px-4 font-semibold text-wedding-text-title">
                  {formatAmount(record.amount, record.currency)}
                </td>
                <td className="py-3 px-4">
                  <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                    record.giftType === 'cash'
                      ? 'bg-green-100 text-green-800'
                      : record.giftType === 'gift'
                      ? 'bg-blue-100 text-blue-800'
                      : 'bg-purple-100 text-purple-800'
                  }`}>
                    {formatGiftType(record.giftType)}
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-wedding-text-body">
                  {record.recordedAt.toLocaleDateString('zh-TW')}
                </td>
                <td className="py-3 px-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => onEdit(record)}
                      className="p-1 text-wedding-pink hover:bg-wedding-pink/10 rounded"
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDelete(record.id)}
                      disabled={deleting === record.id}
                      className="p-1 text-red-500 hover:bg-red-50 rounded disabled:opacity-50"
                    >
                      <Trash2 className={`h-4 w-4 ${deleting === record.id ? 'animate-spin' : ''}`} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredRecords.length === 0 && (
          <div className="text-center py-8 text-wedding-text-body">
            {searchTerm ? '沒有符合搜尋條件的記錄' : '尚無紅包記錄'}
          </div>
        )}
      </div>
    </ThemedCard>
  );
};
